import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { authSelectors } from '../../../../store/selectors/auth';
import { sendLessonVideo } from '../../../../api/programs';
import usePreviewFeature from '../../../../hooks/usePreviewFeature';
import './lesson-video-tile.scss';

const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.7 4.3 3.4 11c-1.2.5-1.2 1.2-.2 1.5l4.4 1.4 1.7 5.2c.2.6.1.8.7.8.5 0 .7-.2 1-.5l2.1-2.1 4.5 3.3c.8.5 1.4.2 1.6-.8l2.9-13.8c.3-1.2-.4-1.8-1.4-1.3Zm-3 3.4-8.3 7.5-.3 3.5-1.4-4.6 9.2-5.8c.4-.3.8-.1.8-.6Z" style={{ fill: 'currentColor', stroke: 'none' }} />
  </svg>
);

TelegramIcon.propTypes = { className: PropTypes.string };
TelegramIcon.defaultProps = { className: '' };

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" style={{ fill: 'none' }} />
  </svg>
);

const STEP_CONFIRM = 'confirm';
const STEP_SENDING = 'sending';
const STEP_DONE = 'done';
const STEP_ERROR = 'error';

const LessonVideoTile = ({ lesson }) => {
  const isEnabled = usePreviewFeature('lesson-telegram-video');
  const auth = useSelector(authSelectors);
  const [step, setStep] = useState(null);
  const [message, setMessage] = useState('');
  const [botUrl, setBotUrl] = useState('');

  const isOpen = step !== null;
  const isSending = step === STEP_SENDING;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !isSending) setStep(null);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isSending]);

  if (!isEnabled || !(lesson.group_video_count > 0)) {
    return null;
  }

  const lessonName = lesson.title || lesson.topic || lesson.program_title || lesson.name || 'заняття';
  const close = () => {
    if (!isSending) setStep(null);
  };

  const send = async () => {
    setStep(STEP_SENDING);

    try {
      const response = await sendLessonVideo({ eventId: lesson.group_video_event_id || 0, token: auth.token });
      setMessage(response?.data?.info?.message || 'Запис надіслано у ваш Telegram-бот.');
      setBotUrl(response?.data?.data?.bot_url || '');
      setStep(STEP_DONE);
    } catch (error) {
      const data = error?.response?.data;
      setMessage(data?.info?.message || 'Не вдалося надіслати запис. Перевірте інтернет і спробуйте ще раз.');
      setBotUrl(data?.data?.bot_url || '');
      setStep(STEP_ERROR);
    }
  };

  return (
    <>
      <button
        type="button"
        className="course-tile course-tile--tg"
        onClick={() => setStep(STEP_CONFIRM)}
        title="Отримати запис заняття в Telegram"
      >
        <TelegramIcon className="course-tile__icon" />
        <span className="course-tile__label">Запис у TG</span>
      </button>

      {isOpen && createPortal(
        <div
          className="lvt-overlay"
          role="presentation"
          onClick={(event) => {
            if (event.target === event.currentTarget) close();
          }}
        >
          <div className="lvt-modal" role="dialog" aria-modal="true" aria-labelledby="lvt-modal-title">
            <div className="lvt-modal__head">
              <span className="lvt-modal__icon"><TelegramIcon /></span>
              <h2 className="lvt-modal__title" id="lvt-modal-title">Запис заняття</h2>
              <button type="button" className="lvt-modal__close" aria-label="Закрити" onClick={close} disabled={isSending}>
                <CloseIcon />
              </button>
            </div>

            {(step === STEP_CONFIRM || step === STEP_SENDING) && (
              <>
                <p className="lvt-modal__text">
                  Ви дійсно хочете отримати запис «<b>{lessonName}</b>»
                  {lesson.event_date ? ` від ${lesson.event_date}` : ''} у Telegram-бот?
                </p>
                <div className="lvt-modal__actions">
                  <button type="button" className="lvt-btn lvt-btn--main" onClick={send} disabled={isSending}>
                    {isSending ? 'Надсилаємо…' : 'Так, надіслати'}
                  </button>
                  <button type="button" className="lvt-btn" onClick={close} disabled={isSending}>
                    Скасувати
                  </button>
                </div>
              </>
            )}

            {(step === STEP_DONE || step === STEP_ERROR) && (
              <>
                <p className={`lvt-modal__text ${step === STEP_ERROR ? 'lvt-modal__text--error' : 'lvt-modal__text--ok'}`}>
                  {message}
                </p>
                <div className="lvt-modal__actions">
                  {botUrl && (
                    <a className="lvt-btn lvt-btn--main" href={botUrl} target="_blank" rel="noopener noreferrer">
                      Відкрити бот
                    </a>
                  )}
                  {step === STEP_ERROR && (
                    <button type="button" className={`lvt-btn ${botUrl ? '' : 'lvt-btn--main'}`} onClick={send}>
                      Спробувати ще раз
                    </button>
                  )}
                  <button type="button" className="lvt-btn" onClick={close}>
                    Закрити
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

LessonVideoTile.propTypes = {
  lesson: PropTypes.object.isRequired,
};

export default LessonVideoTile;
